import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sliders, Trophy, RotateCcw, Plus, Trash2 } from 'lucide-react';
import { PointsConfig, RankTier, defaultPointsConfig, pointsForFunding, tierForPoints } from './types';
interface AdminSettingsModalProps {
  open: boolean;
  config: PointsConfig;
  onClose: () => void;
  onSave: (cfg: PointsConfig) => void;
}
export function AdminSettingsModal({
  open,
  config,
  onClose,
  onSave
}: AdminSettingsModalProps) {
  const [draft, setDraft] = useState<PointsConfig>(config);
  useEffect(() => {
    if (open) setDraft(config);
  }, [open, config]);
  const updateTier = (id: string, patch: Partial<RankTier>) => {
    setDraft(d => ({
      ...d,
      tiers: d.tiers.map(t => t.id === id ? {
        ...t,
        ...patch
      } : t)
    }));
  };
  const removeTier = (id: string) => {
    setDraft(d => ({
      ...d,
      tiers: d.tiers.filter(t => t.id !== id)
    }));
  };
  const addTier = () => {
    const max = draft.tiers.reduce((m, t) => Math.max(m, t.minPoints), 0);
    setDraft(d => ({
      ...d,
      tiers: [...d.tiers, {
        id: `t-${Date.now()}`,
        name: 'New Tier',
        minPoints: max + 500
      }]
    }));
  };
  const reset = () => setDraft(defaultPointsConfig);
  const save = () => {
    // sort tiers by minPoints ascending before saving
    const sorted = {
      ...draft,
      tiers: [...draft.tiers].sort((a, b) => a.minPoints - b.minPoints)
    };
    onSave(sorted);
    onClose();
  };
  // Preview the formula for a few sample loan sizes
  const samples = [250000, 500000, 1000000];
  return <AnimatePresence data-id="element-18">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50" onClick={onClose} data-id="element-19" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-20">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} className="pointer-events-auto w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-hidden bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl flex flex-col" data-id="element-21">
              <div className="px-6 pt-6 pb-4 border-b border-slate-800 flex items-start justify-between" data-id="element-22">
                <div className="flex items-center gap-3" data-id="element-23">
                  <div className="p-2.5 rounded-xl bg-orange-500/15 text-orange-400 border border-orange-500/30" data-id="element-24">
                    <Sliders className="w-5 h-5" data-id="element-25" />
                  </div>
                  <div data-id="element-26">
                    <p className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-0.5" data-id="element-27">
                      Admin Settings
                    </p>
                    <h2 className="text-xl font-bold font-display text-white" data-id="element-28">
                      Points & Rank Configuration
                    </h2>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white" data-id="element-29">
                  <X className="w-5 h-5" data-id="element-30" />
                </button>
              </div>

              <div className="overflow-y-auto p-6 flex-1 space-y-6" data-id="element-31">
                {/* Points formula */}
                <section data-id="element-32">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-300 mb-3" data-id="element-33">
                    Points Formula
                  </h3>
                  <div className="grid grid-cols-2 gap-3" data-id="element-34">
                    <Field label="Base Points (per funded loan)" value={draft.basePoints} onChange={v => setDraft(d => ({
                  ...d,
                  basePoints: v
                }))} suffix="pts" data-id="element-35" />
                    <Field label="Points per $1k funded" value={draft.pointsPerThousand} onChange={v => setDraft(d => ({
                  ...d,
                  pointsPerThousand: v
                }))} suffix="pts / $1k" step={0.01} data-id="element-36" />
                  </div>
                  <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-id="element-37">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2" data-id="element-38">
                      Preview
                    </p>
                    <div className="grid grid-cols-3 gap-2" data-id="element-39">
                      {samples.map(s => <div key={s} className="text-center rounded bg-slate-800/50 p-2" data-id="element-40">
                          <p className="text-[11px] text-slate-400" data-id="element-41">
                            ${(s / 1000).toFixed(0)}k loan
                          </p>
                          <p className="text-lg font-bold text-orange-400" data-id="element-42">
                            {pointsForFunding(s, draft)} pts
                          </p>
                        </div>)}
                    </div>
                  </div>
                </section>

                {/* Rank tiers */}
                <section data-id="element-43">
                  <div className="flex items-center justify-between mb-3" data-id="element-44">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2" data-id="element-45">
                      <Trophy className="w-4 h-4 text-yellow-400" data-id="element-46" />
                      Rank Tiers
                    </h3>
                    <button onClick={addTier} className="flex items-center gap-1 text-xs font-semibold text-orange-300 hover:text-orange-200" data-id="element-47">
                      <Plus className="w-3.5 h-3.5" data-id="element-48" /> Add Tier
                    </button>
                  </div>

                  <div className="space-y-2" data-id="element-49">
                    {[...draft.tiers].sort((a, b) => a.minPoints - b.minPoints).map((t, idx) => <div key={t.id} className="grid grid-cols-[24px_1fr_140px_36px] gap-2 items-center rounded-lg border border-slate-800 bg-slate-950/40 p-2.5" data-id="element-50">
                          <div className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold ${tierBadgeClass(idx)}`} data-id="element-51">
                            {idx + 1}
                          </div>
                          <input type="text" value={t.name} onChange={e => updateTier(t.id, {
                    name: e.target.value
                  })} className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500/50" data-id="element-52" />
                          <div className="relative" data-id="element-53">
                            <input type="number" value={t.minPoints} min={0} onChange={e => updateTier(t.id, {
                      minPoints: Number(e.target.value) || 0
                    })} className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500/50 pr-12" data-id="element-54" />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 uppercase font-bold" data-id="element-55">
                              min pts
                            </span>
                          </div>
                          <button onClick={() => removeTier(t.id)} disabled={draft.tiers.length <= 1} className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed" title="Remove tier" data-id="element-56">
                            <Trash2 className="w-4 h-4" data-id="element-57" />
                          </button>
                        </div>)}
                  </div>

                  <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-id="element-58">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2" data-id="element-59">
                      Sample LO at various point totals
                    </p>
                    <div className="grid grid-cols-3 gap-2" data-id="element-60">
                      {[500, 1500, 3000].map(p => {
                    const tier = tierForPoints(p, draft);
                    return <div key={p} className="text-center rounded bg-slate-800/50 p-2" data-id="element-61">
                            <p className="text-[11px] text-slate-400" data-id="element-62">
                              {p} pts
                            </p>
                            <p className="text-sm font-bold text-yellow-300" data-id="element-63">
                              {tier.name}
                            </p>
                          </div>;
                  })}
                    </div>
                  </div>
                </section>
              </div>

              <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between bg-slate-950/50" data-id="element-64">
                <button onClick={reset} className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white" data-id="element-65">
                  <RotateCcw className="w-3.5 h-3.5" data-id="element-66" />
                  Reset to defaults
                </button>
                <div className="flex items-center gap-2" data-id="element-67">
                  <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-300 hover:bg-slate-800" data-id="element-68">
                    Cancel
                  </button>
                  <button onClick={save} className="px-5 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 text-white text-sm font-bold shadow-lg shadow-orange-500/25" data-id="element-69">
                    Save Changes
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}
function Field({
  label,
  value,
  onChange,
  suffix,
  step = 1
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  step?: number;
}) {
  return <label className="block" data-id="element-70">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-71">
        {label}
      </span>
      <div className="relative mt-1" data-id="element-72">
        <input type="number" value={value} step={step} min={0} onChange={e => onChange(Number(e.target.value) || 0)} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500/50 pr-16" data-id="element-73" />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 uppercase font-bold" data-id="element-74">
            {suffix}
          </span>}
      </div>
    </label>;
}
function tierBadgeClass(idx: number): string {
  // bronze / silver / gold / platinum-ish progression
  const styles = ['bg-gradient-to-br from-orange-700 to-orange-900 text-orange-100', 'bg-gradient-to-br from-slate-400 to-slate-600 text-slate-900', 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-yellow-950', 'bg-gradient-to-br from-cyan-300 to-cyan-500 text-cyan-950', 'bg-gradient-to-br from-purple-400 to-purple-600 text-purple-950', 'bg-gradient-to-br from-pink-400 to-pink-600 text-pink-950'];
  return styles[idx % styles.length];
}